import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

export interface KycFormData {
  country: string
  last_name: string
  first_name: string
  birthYear: string
  birthMonth: string
  birthDay: string
  identity_type: string
  passport: string | null
  id_card: {
    back: string | null
    front: string | null
    hand_hold: string | null
  }
}

const initialFormData: KycFormData = {
  country: '',
  last_name: '',
  first_name: '',
  birthYear: '',
  birthMonth: '',
  birthDay: '',
  identity_type: '',
  passport: null,
  id_card: {
    back: null,
    front: null,
    hand_hold: null
  }
}

export const useKycForm = () => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<KycFormData>(initialFormData)

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // 表单校验逻辑
  const validateBaseInfoForm = () => {
    const { country, last_name, first_name, birthYear, birthMonth, birthDay } = formData

    // 检查所有必填字段是否已填写
    if (!country || !last_name || !first_name || !birthYear || !birthMonth || !birthDay) {
      return false
    }

    // 检查姓名长度（至少1个字符）
    if (last_name.trim().length === 0 || first_name.trim().length === 0) {
      return false
    }

    // 检查出生日期的有效性
    const year = parseInt(birthYear)
    const month = parseInt(birthMonth)
    const day = parseInt(birthDay)

    if (year < 1900 || year > new Date().getFullYear()) {
      return false
    }

    if (month < 1 || month > 12) {
      return false
    }

    if (day < 1 || day > 31) {
      return false
    }

    // 检查日期是否存在（简单验证）
    const date = new Date(year, month - 1, day)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return false
    }

    return true
  }

  // 独立的证件信息校验函数
  const validateIdentificationForm = () => {
    const { identity_type, passport, id_card } = formData

    if (!identity_type) {
      return { isValid: false, message: t('请选择证件类型') }
    }

    if (identity_type === 'passport') {
      if (!passport) {
        return { isValid: false, message: t('请上传护照') }
      }
    } else if (identity_type === 'id_card') {
      if (!id_card.front) {
        return { isValid: false, message: t('请上传身份证正面') }
      }
      if (!id_card.back) {
        return { isValid: false, message: t('请上传身份证背面') }
      }
      if (!id_card.hand_hold) {
        return { isValid: false, message: t('请上传手持身份证照片') }
      }
    }

    return { isValid: true, message: '' }
  }

  // 检查表单是否有效
  const isBaseInfoFormValid = useMemo(() => {
    return validateBaseInfoForm()
  }, [formData])

  // 检查证件信息是否完整
  const isIdentificationFormValid = useMemo(() => {
    const { identity_type, passport, id_card } = formData

    if (!identity_type) {
      return false
    }

    if (identity_type === 'passport') {
      return !!passport
    } else if (identity_type === 'id_card') {
      return !!(id_card.front && id_card.back && id_card.hand_hold)
    }

    return false
  }, [formData])

  // 综合校验：基本信息和证件信息都完整才能提交
  const isFormCompletelyValid = useMemo(() => {
    return isBaseInfoFormValid && isIdentificationFormValid
  }, [isBaseInfoFormValid, isIdentificationFormValid])

  return {
    formData,
    setFormData,
    handleInputChange,
    validateBaseInfoForm,
    validateIdentificationForm,
    isBaseInfoFormValid,
    isIdentificationFormValid,
    isFormCompletelyValid
  }
}